import { motion } from "framer-motion";
import {
  ArrowRight,
  Blocks,
  Clock3,
  Globe2,
  Hospital,
  LayoutDashboard,
  LayoutGrid,
  Lightbulb,
  Microscope,
  Network,
  Radio,
  Rocket,
  ShieldCheck,
  SlidersHorizontal,
  Pill,
  Stethoscope,
  Workflow,
} from "lucide-react";
import PulsingPillIcon from "./PulsingPillIcon";
import { primaryCtaClass } from "../utils/ctaClasses";
import { slowFadeUp, slowStagger } from "../utils/animations";

const GHEALTH_ESYA_GRAPHIC = `${process.env.PUBLIC_URL}/images/gestalt-platform-evolution-v3.png`;
const ESYA_HUB_GRAPHIC = `${process.env.PUBLIC_URL}/images/esya-hub-graphic.png`;

export default function UseCases() {
  const providerCards = [
    {
      name: "Pharmacies",
      tagline: "Smarter inventory. Better care.",
      description:
        "Streamline inventory, manage prescriptions, and automate billing with full visibility.",
      accent: "bg-amber-50 text-amber-600",
      Icon: Pill,
    },
    {
      name: "Diagnostic Labs",
      tagline: "Faster reports. Better accuracy.",
      description:
        "Digitize lab operations from sample collection to report delivery with seamless integrations.",
      accent: "bg-violet-50 text-violet-600",
      Icon: Microscope,
    },
    {
      name: "Clinics & Small Practices",
      tagline: "Simple, efficient, and effective.",
      description:
        "Designed for small to mid-size clinics to manage patients, appointments, and billing.",
      accent: "bg-emerald-50 text-emerald-600",
      Icon: Stethoscope,
    },
    {
      name: "Hospital Administrators",
      tagline: "Data-driven decisions. Greater control.",
      description:
        "Get a complete view of operations, finances, and performance to make smarter decisions.",
      accent: "bg-cyan-50 text-cyan-600",
      Icon: LayoutDashboard,
    },
    {
      name: "Large Hospitals",
      tagline: "Complex operations. Simplified.",
      description:
        "Manage multiple departments, locations, and thousands of patients in one unified system.",
      accent: "bg-blue-50 text-blue-600",
      Icon: Hospital,
    },
  ];

  return (
    <section
      id="use-cases"
      className="section-viewport scroll-mt-24 py-12 md:py-20 bg-white px-4 md:px-6"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
            <PulsingPillIcon icon={LayoutGrid} tone="blue" />
            Use Cases
          </span>
          <h2 className="mt-4 text-section font-bold text-secondary leading-tight">
            Built for
            <br />
            <span className="bg-gradient-to-r from-accent to-blue-500 bg-clip-text text-transparent">
              Every Healthcare Provider
            </span>
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-muted">
            From large hospitals to specialty clinics, our platform adapts to your
            workflows and scales with your needs.
          </p>
        </div>

        <motion.div
          variants={slowStagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5 items-stretch"
        >
          {providerCards.map((card, index) => {
            const CardIcon = card.Icon;
            return (
              <motion.article
              variants={slowFadeUp}
              key={card.name}
              className={`relative flex h-full min-h-0 flex-row gap-3 p-4 md:p-5 xl:flex-col xl:gap-0 transition hover:-translate-y-1 ${
                index < providerCards.length - 1
                  ? "border-b border-slate-200 pb-6 md:pb-5 xl:border-b-0 xl:pb-5 xl:after:absolute xl:after:-right-2.5 xl:after:top-6 xl:after:h-[calc(100%-3rem)] xl:after:w-px xl:after:bg-slate-200 xl:after:content-['']"
                  : ""
              }`}
            >
              <div
                className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${card.accent}`}
              >
                <CardIcon className="size-5" strokeWidth={1.75} aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className={`text-base font-semibold xl:mt-3 ${card.accent.split(" ")[1]}`}>
                  {card.name}
                </h3>
                <p className="mt-1 text-sm font-medium text-black">{card.tagline}</p>
                <p className="mt-1.5 text-sm text-muted leading-6 xl:mt-2">
                  {card.description}
                </p>
              </div>
            </motion.article>
            );
          })}
        </motion.div>

        <div className="mt-10 text-center md:mt-12">
          <p className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
            <PulsingPillIcon icon={Workflow} tone="blue" />
            Our core system
          </p>
        </div>

        <div
          id="core-system"
          className="relative isolate mt-4 scroll-mt-28 overflow-hidden rounded-3xl p-4 md:mt-5 md:p-6"
        >
          <div className="text-center">
            <h3 className="mt-4 text-section font-bold text-secondary leading-tight">
              From GHealth to eSya –
              <span className="block bg-gradient-to-r from-accent to-blue-500 bg-clip-text text-transparent">
                The Evolution Continues
              </span>
            </h3>
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-relaxed text-slate-600 md:text-base">
              We started by solving individual healthcare challenges. Today, we
              deliver a fully connected, cloud-based ecosystem built from years
              of real-world learning.
            </p>

            <div className="mt-5 w-full rounded-2xl p-3 md:p-4">
              <div>
                <h2 className="font-display text-2xl font-bold text-slate-900 md:text-3xl">
                  Our Journey of Building Solutions
                </h2>

                <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
                  <article className="relative rounded-xl p-4 md:after:absolute md:after:-right-2 md:after:top-4 md:after:h-[calc(100%-2rem)] md:after:w-px md:after:bg-slate-200 md:after:content-['']">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-sm font-bold text-blue-700">
                      1
                    </span>
                    <h3 className="mt-3 text-sm font-semibold text-slate-900">
                      Building Specialized Systems
                    </h3>
                    <p className="mt-1 text-xs leading-relaxed text-slate-600">
                      We created focused solutions for patient care, operations,
                      inventory, and finance
                    </p>
                  </article>

                  <article className="relative rounded-xl p-4 md:after:absolute md:after:-right-2 md:after:top-4 md:after:h-[calc(100%-2rem)] md:after:w-px md:after:bg-slate-200 md:after:content-['']">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-sm font-bold text-violet-700">
                      2
                    </span>
                    <h3 className="mt-3 text-sm font-semibold text-slate-900">
                      Real-World Implementations
                    </h3>
                    <p className="mt-1 text-xs leading-relaxed text-slate-600">
                      Deployed across multiple environments, learning from real
                      usage
                    </p>
                  </article>

                  <article className="relative rounded-xl p-4">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-sm font-bold text-emerald-700">
                      3
                    </span>
                    <h3 className="mt-3 text-sm font-semibold text-slate-900">
                      A Critical Insight
                    </h3>
                    <p className="mt-1 text-xs leading-relaxed text-slate-600">
                      Systems need connection, not just features
                    </p>
                  </article>
                </div>
              </div>

              <div className="mt-8 border-t border-blue-100/80 pt-6">
                <div className="p-5 md:p-6">
                  <h2 className="font-display text-2xl font-bold text-slate-900 md:text-3xl">
                    Introducing GHealth — Our First Unified Platform
                  </h2>
                  <p className="mx-auto mt-2 max-w-3xl text-center text-sm leading-relaxed text-slate-700 md:text-base">
                    We brought everything together into a single enterprise-grade
                    system designed to connect workflows and data seamlessly.
                  </p>

                  <div className="mt-5 grid grid-cols-1 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
                    <div className="flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-blue-900">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                        <Network className="size-5" strokeWidth={1.9} aria-hidden />
                      </span>
                      <span>Unified Data Across Departments</span>
                    </div>
                    <div className="flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-violet-900">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                        <SlidersHorizontal className="size-5" strokeWidth={1.9} aria-hidden />
                      </span>
                      <span>Standardized Workflows</span>
                    </div>
                    <div className="flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-emerald-900">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                        <Rocket className="size-5" strokeWidth={1.9} aria-hidden />
                      </span>
                      <span>Enterprise Scalability</span>
                    </div>
                    <div className="flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-amber-900">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
                        <Clock3 className="size-5" strokeWidth={1.9} aria-hidden />
                      </span>
                      <span>Improved Operational Efficiency</span>
                    </div>
                  </div>

                </div>
              </div>

              <div className="mt-8 border-t border-blue-100/80 pt-6">
                <div
                  className="relative isolate overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50/70 to-blue-50/50 px-5 py-8 shadow-lg shadow-slate-900/5 md:px-8 md:py-10"
                >
                  <div
                    className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-blue-400/15 blur-3xl"
                    aria-hidden
                  />
                  <div
                    className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-indigo-400/10 blur-3xl"
                    aria-hidden
                  />
                  <div
                    className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(59,130,246,0.08),transparent)]"
                    aria-hidden
                  />
                  <div className="relative z-10 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-8">
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
                      <Lightbulb className="size-5" strokeWidth={1.9} aria-hidden />
                    </div>
                    <h2 className="mt-3 font-display text-2xl font-bold text-slate-900 md:text-3xl">
                      What We Learnt
                    </h2>
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base">
                      Every deployment taught us something new. Every challenge
                      helped us refine our vision.
                    </p>
                  </div>

                  <div>
                    <ul className="text-sm font-medium text-slate-700 md:text-base">
                      <li className="flex items-center gap-3 py-3 text-blue-900">
                        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                          <Globe2 className="size-5" aria-hidden />
                        </span>
                        <span>Need for global scalability</span>
                      </li>
                      <li className="flex items-center gap-3 py-3 text-violet-900">
                        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                          <Radio className="size-5" aria-hidden />
                        </span>
                        <span>Demand for real-time access</span>
                      </li>
                      <li className="flex items-center gap-3 py-3 text-emerald-900">
                        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                          <Blocks className="size-5" aria-hidden />
                        </span>
                        <span>Importance of modular systems</span>
                      </li>
                      <li className="flex items-center gap-3 py-3 text-rose-900">
                        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-700">
                          <ShieldCheck className="size-5" aria-hidden />
                        </span>
                        <span>Critical role of security and compliance</span>
                      </li>
                    </ul>
                  </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 border-t border-blue-100/80 pt-6">
                <div className="rounded-2xl bg-white p-5 md:p-6">
                  <img
                    src={GHEALTH_ESYA_GRAPHIC}
                    alt="Platform evolution of GestAlt from specialized systems to GHealth and next-gen cloud eSya."
                    className="w-full rounded-lg object-contain object-center"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>

            </div>
          </div>

        </div>

        <motion.div
          variants={slowFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="relative isolate mt-8 overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50/70 to-blue-50/50 px-6 py-8 shadow-lg shadow-slate-900/5 md:mt-8 md:px-10 md:py-10"
        >
          <div
            className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-blue-400/15 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-indigo-400/10 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(59,130,246,0.08),transparent)]"
            aria-hidden
          />

          <motion.div className="relative z-10 grid grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_minmax(0,18rem)_auto] lg:gap-10">
            <motion.div className="min-w-0 text-center lg:text-left">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-slate-500 md:text-xs">
                From experience to evolution
              </p>
              <h3 className="mt-3 font-display text-2xl font-bold text-slate-900 md:text-3xl">
                eSya — The Cloud-Native Evolution
              </h3>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base lg:mx-0">
                A next-generation platform built by combining years of
                experience, implementation insights, and continuous
                improvement.
              </p>
            </motion.div>

            <motion.div className="mx-auto flex w-full max-w-[16rem] justify-center lg:mx-0 lg:max-w-none lg:justify-end">
              <img
                src={ESYA_HUB_GRAPHIC}
                alt="eSya connected hub: clinical, operations, analytics, integrations, and telehealth modules in a unified platform."
                width={440}
                height={360}
                decoding="async"
                className="h-auto w-full object-contain object-center"
              />
            </motion.div>

            <motion.div className="flex flex-col items-center gap-4 lg:items-end">
              <a
                href="#demo-form"
                className={`${primaryCtaClass} w-full gap-2 px-5 py-3 text-sm font-bold sm:w-auto`}
              >
                Connect with us
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
              <p className="max-w-[14rem] text-center text-xs text-slate-500 lg:text-right">
                Request a personalized demo for your organization.
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
